import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";
import { Button, Caption1, Label } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { VersionItem } from "~/components/Note/VersionItem";
import { useNoteVersionRenameMutation, useNoteVersions } from "~/repository";
import Angie from "~/utils/angie";
import { Divider } from "../ModalObjects";

export const Versions = ({ projectId, id, onRevert }) => {
  const { t } = useTranslation();
  const { data = [], isLoading } = useNoteVersions(projectId, id);

  const [showAll, toggle] = useToggle(false);

  const shownVersions = useMemo(() => {
    let ver = [...data];
    if (!showAll && data.length > 3) ver.length = 3;
    return ver;
  }, [data, showAll]);

  const handleClick = useCallback(
    (modificationId) => {
      Angie.logger.info("Note version clicked.", {
        note_version: {
          note_id: id,
          modification_id: modificationId,
        },
      });
    },
    [id]
  );
  const renameMutation = useNoteVersionRenameMutation(projectId, id);

  const handleRenameVersion = useCallback(
    (data) => {
      renameMutation.mutate(data);
    },
    [renameMutation]
  );

  if (isLoading || (!isLoading && data.length < 2)) return null;

  return (
    <>
      <Divider className="tw-my-5" />
      <div className="tw-max-w-[280px] ">
        <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
          <Label size="small">{t("Versions")}</Label>
          <Caption1 color="tertiary">{t("Click to compare")}</Caption1>
        </div>
        {shownVersions.map((version, index) => {
          return (
            <VersionItem
              key={`${version.modification_id}-${id}`}
              className={classNames("tw-mb-1", { "tw-mt-1": index > 0 })}
              versionName={version.version_name}
              modificationId={version.modification_id}
              createdByName={version.created_by_name}
              createdByEmail={version.created_by_email}
              createdOn={version.created_on}
              createdById={version.created_by_id}
              id={version.version_id}
              onClick={handleClick}
              onRename={handleRenameVersion}
              onRevert={data.length !== version.version_id ? onRevert : undefined}
            />
          );
        })}
        {!isLoading && data.length > 3 ? (
          <div className="tw-mt-2">
            <Button
              variant="tertiary"
              size="small"
              onClick={toggle}
              className="-tw-ml-2"
            >
              {showAll ? t("Show less") : `${t("Show")} ${data.length - 3} ${t("more")}`}
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
};

Versions.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  onRevert: PropTypes.func,
};
